'use client';

import { createPortal } from 'react-dom';
import {
  MouseEvent as ReactMouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { twMerge } from 'tailwind-merge';

type BaseMenuItem = {
  key?: string;
  label: ReactNode;
  icon?: ReactNode;
  shortcut?: string;
  onSelect?: () => void;
  disabled?: boolean;
  danger?: boolean;
};

type SeparatorEntry = { type: 'separator'; id?: string };

export type ContextMenuEntry = BaseMenuItem | SeparatorEntry;

const isSeparatorEntry = (entry: ContextMenuEntry): entry is SeparatorEntry =>
  typeof entry === 'object' && 'type' in entry && entry.type === 'separator';

export interface ContextMenuProps {
  trigger?: ReactNode;
  items: ContextMenuEntry[];
  menuClassName?: string;
  itemClassName?: string;
  separatorClassName?: string;
  wrapperClassName?: string;
  closeOnSelect?: boolean;
  openOnClick?: boolean;
  onOpenChange?: (open: boolean) => void;
}

type MenuPosition = {
  top: number;
  left: number;
};

export const ContextMenu = ({
  trigger,
  items,
  menuClassName,
  itemClassName,
  separatorClassName,
  wrapperClassName,
  closeOnSelect = true,
  openOnClick = false,
  onOpenChange,
}: ContextMenuProps) => {
  const [isMounted, setIsMounted] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);
  const [isPositionReady, setIsPositionReady] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  const notifyOpenChange = useCallback(
    (open: boolean) => {
      onOpenChange?.(open);
    },
    [onOpenChange]
  );

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    setMenuPosition(null);
    setIsPositionReady(false);
    notifyOpenChange(false);
  }, [notifyOpenChange]);

  const openMenu = useCallback(
    (event: ReactMouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      setIsPositionReady(false);
      setIsOpen(true);
      notifyOpenChange(true);
    },
    [notifyOpenChange]
  );

  const handleContextMenu = useCallback(
    (event: ReactMouseEvent) => {
      openMenu(event);
    },
    [openMenu]
  );

  const handleClickTrigger = useCallback(
    (event: ReactMouseEvent) => {
      if (!openOnClick) return;
      openMenu(event);
    },
    [openOnClick, openMenu]
  );

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        closeMenu();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };

    const handleWindowResize = () => {
      closeMenu();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('contextmenu', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleWindowResize);
    window.addEventListener('scroll', handleWindowResize, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('contextmenu', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleWindowResize);
      window.removeEventListener('scroll', handleWindowResize, true);
    };
  }, [closeMenu, isOpen]);

  useLayoutEffect(() => {
    if (!isOpen || !menuRef.current || !triggerRef.current) return;

    // Use double RAF to ensure menu is fully rendered and measured
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!menuRef.current || !triggerRef.current) return;

        const triggerRect = triggerRef.current.getBoundingClientRect();
        const menuRect = menuRef.current.getBoundingClientRect();
        const OFFSET = 4;

        const left = triggerRect.right - menuRect.width;
        const top = triggerRect.top - menuRect.height - OFFSET;

        setMenuPosition({
          left: Math.max(8, left),
          top: Math.max(8, top),
        });
        setIsPositionReady(true);
      });
    });
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const firstItem = menuRef.current?.querySelector<HTMLButtonElement>('button[data-menu-item]');
    firstItem?.focus();
  }, [isOpen, items]);

  return (
    <>
      <div ref={triggerRef} onContextMenu={handleContextMenu} onClick={handleClickTrigger} className={wrapperClassName}>
        {trigger ?? (
          <button
            type="button"
            className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-300 transition hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
            aria-label="Open agent actions"
          >
            <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
              <circle cx="4" cy="10" r="1.5" />
              <circle cx="10" cy="10" r="1.5" />
              <circle cx="16" cy="10" r="1.5" />
            </svg>
          </button>
        )}
      </div>
      {isMounted &&
        isOpen &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            tabIndex={-1}
            className={twMerge(
              'flex flex-col gap-y-2 p-2 fixed z-[1000] min-w-[120px] rounded-lg border border-neutral-900 bg-neutral-1000 text-sm text-neutral-200',
              !isPositionReady && 'invisible',
              menuClassName
            )}
            style={menuPosition ? { top: menuPosition.top, left: menuPosition.left } : undefined}
          >
            {items.map((item, index) => {
              if (isSeparatorEntry(item)) {
                return (
                  <div
                    key={item.id ?? `separator-${index}`}
                    className={twMerge('h-px bg-neutral-800', separatorClassName)}
                    role="separator"
                  />
                );
              }

              const key = item.key ?? `context-item-${index}`;
              const handleSelect = () => {
                if (item.disabled) return;
                item.onSelect?.();
                if (closeOnSelect) {
                  closeMenu();
                }
              };

              return (
                <button
                  key={key}
                  type="button"
                  role="menuitem"
                  data-menu-item
                  onClick={handleSelect}
                  disabled={item.disabled}
                  className={twMerge(
                    'flex w-full items-center justify-between gap-4 rounded-lg text-neutral-300 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60',
                    item.disabled ? 'cursor-not-allowed text-neutral-500' : 'cursor-pointer hover:text-white',
                    item.danger && !item.disabled ? 'text-error hover:text-error' : '',
                    itemClassName
                  )}
                >
                  <span className="flex items-center gap-2">
                    {item.icon && <span className="text-lg text-neutral-400">{item.icon}</span>}
                    <span>{item.label}</span>
                  </span>
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </>
  );
};

export default ContextMenu;
